// src/components/docs/CvDocs.jsx
import React, { useMemo, useState } from "react";
import { CopyBlock, dracula } from "react-code-blocks";
import {
  HiChevronRight,
  HiChevronDown,
  HiOutlineCode,
  HiOutlineLink,
} from "react-icons/hi";

// --- listas/constantes (helpers de documentação) ---
const NATUREZAS = [
  { Codigo: 60, Sigla: "CI", Descricao: "CÉDULA DE CRÉDITO INDUSTRIAL" },
  { Codigo: 13, Sigla: "CR", Descricao: "CÉDULA RURAL PIGNORATÍCIA" },
  { Codigo: 59, Sigla: "CH", Descricao: "CHEQUE" },
  { Codigo: 6, Sigla: "CB", Descricao: "CONTRATO DE CÂMBIO" },
  { Codigo: 61, Sigla: "CS", Descricao: "CONTRATOS DIVERSOS" },
  { Codigo: 8, Sigla: "CD", Descricao: "CREDIÁRIO" },
  { Codigo: 12, Sigla: "CP", Descricao: "CRÉDITO PESSOAL" },
  { Codigo: 62, Sigla: "DB", Descricao: "DEBENTURES" },
  { Codigo: 24, Sigla: "DP", Descricao: "DUPLICATA" },
  { Codigo: 63, Sigla: "LC", Descricao: "LETRA DE CÂMBIO" },
  { Codigo: 64, Sigla: "NE", Descricao: "NOTA CRÉDITO EXPORTAÇÃO" },
  { Codigo: 65, Sigla: "NP", Descricao: "NOTA PROMISSÓRIA" },
  { Codigo: 66, Sigla: "XX", Descricao: "OUTRAS OPERAÇÕES" },
  // DC (22) é usado nas regras de cheque
  {
    Codigo: 22,
    Sigla: "DC",
    Descricao: "DEVOLUÇÃO DE CHEQUES (regras específicas)",
  },
];

const FIELDS = [
  {
    nome: "cnpjClienteIndireto",
    tipo: "string (CNPJ dígitos)",
    obrigatorio: "Sim",
    obs: "CNPJ do Credor",
  },
  {
    nome: "documentoDevedor",
    tipo: "string (CPF/CNPJ dígitos)",
    obrigatorio: "Sim",
    obs: "CPF ou CNPJ do devedor.",
  },
  {
    nome: "codigoNatureza",
    tipo: "string|number",
    obrigatorio: "Sim",
    obs: "Use um dos códigos de natureza (tabela ao lado).",
  },
  {
    nome: "dataVencimento",
    tipo: "date (YYYY-MM-DD)",
    obrigatorio: "Sim",
    obs: "Entre hoje-4a11m e hoje-4d (regras do seu front).",
  },
  {
    nome: "valor",
    tipo: "number (decimal)",
    obrigatorio: "Sim",
    obs: "Ex.: 2500.50",
  },
  {
    nome: "contrato",
    tipo: "string",
    obrigatorio: "Sim",
    obs: "Até 16 chars (0-9 A-Z / - .) conforme validação do front.",
  },

  // Cheque / DC (22)
  {
    nome: "numeroBanco",
    tipo: "string",
    obrigatorio: "Condicional",
    obs: "Obrigatório se natureza = DC (22).",
  },
  {
    nome: "numeroAgencia",
    tipo: "string (dígitos)",
    obrigatorio: "Condicional",
    obs: "Obrigatório se natureza = DC (22).",
  },
  {
    nome: "numeroCheque",
    tipo: "string (dígitos)",
    obrigatorio: "Condicional",
    obs: "Obrigatório se natureza = DC (22).",
  },
  {
    nome: "numeroAlinea",
    tipo: "string",
    obrigatorio: "Condicional",
    obs: "Obrigatório se natureza = DC (22), quando aplicável.",
  },
  {
    nome: "numeroContaCorrente",
    tipo: "string (dígitos)",
    obrigatorio: "Condicional",
    obs: "Obrigatório se natureza = DC (22).",
  },

  // Pessoa / Endereço / Contato
  {
    nome: "dataNascimento",
    tipo: "date (YYYY-MM-DD)",
    obrigatorio: "Condicional",
    obs: "Obrigatório se devedor for CPF.",
  },
  {
    nome: "nome",
    tipo: "string",
    obrigatorio: "Sim",
    obs: "Nome/Razão Social do devedor.",
  },
  { nome: "nomePai", tipo: "string", obrigatorio: "Não", obs: "" },
  { nome: "nomeMae", tipo: "string", obrigatorio: "Não", obs: "" },
  {
    nome: "endereco",
    tipo: "string",
    obrigatorio: "Sim",
    obs: "Máx. 45 chars no front.",
  },
  {
    nome: "bairro",
    tipo: "string",
    obrigatorio: "Sim",
    obs: "Máx. 20 chars no front.",
  },
  {
    nome: "cidade",
    tipo: "string",
    obrigatorio: "Sim",
    obs: "Máx. 25 chars no front.",
  },
  {
    nome: "cep",
    tipo: "string (8 dígitos)",
    obrigatorio: "Sim",
    obs: "Somente dígitos.",
  },
  { nome: "uf", tipo: "string (2)", obrigatorio: "Sim", obs: "Ex.: SP, RJ." },
  {
    nome: "ddd",
    tipo: "string (2 dígitos)",
    obrigatorio: "Não",
    obs: "Preenchido a partir de telefoneFull (quando usado no front).",
  },
  {
    nome: "telefone",
    tipo: "string (sem DDD)",
    obrigatorio: "Não",
    obs: "Preenchido a partir de telefoneFull (quando usado no front).",
  },
  { nome: "email", tipo: "string (email)", obrigatorio: "Não", obs: "" },

  // Cobrança
  { nome: "usarCobranca", tipo: "boolean", obrigatorio: "Não", obs: "" },
  {
    nome: "tipoCobranca",
    tipo: "string",
    obrigatorio: "Condicional",
    obs: 'Obrigatório se usarCobranca = true. Valores: "S" (Simples) | "E" (Extra-Judicial).',
  },
  {
    nome: "valorJurosMulta",
    tipo: "number (decimal)",
    obrigatorio: "Não",
    obs: "",
  },
  {
    nome: "percentualDesconto",
    tipo: "number (0-100)",
    obrigatorio: "Não",
    obs: "",
  },
];

// 🔻 Motivos de Baixa (para POST /negative/:id/baixa)
const MOTIVOS_BAIXA = [
  {
    Codigo: "01",
    Motivo: "Pagamento da dívida",
    Descricao: "O devedor pagou a dívida.",
  },
  {
    Codigo: "03",
    Motivo: "Determinação Judicial",
    Descricao: "Baixa da anotação para cumprimento de determinação judicial.",
  },
  {
    Codigo: "05",
    Motivo: "Ordem Depto Jurídico",
    Descricao: "Ordem Depto Jurídico",
  },
  {
    Codigo: "06",
    Motivo: "Documento Roubado/BO",
    Descricao: "Documento Roubado",
  },
  {
    Codigo: "09",
    Motivo: "Falecimento do Devedor",
    Descricao: "Falecimento do Devedor",
  },
  { Codigo: "26", Motivo: "Pagamento Inibido", Descricao: "Pagamento Inibido" },
];

const Endpoints = [
  {
    method: "POST",
    path: "/api-rscore/negative-create-customer",
    title: "Criar Cliente Indireto",
    desc: "Cria um cliente indireto no provedor antes de registrar negativações.",
    bodyExample: {
      CnpjIndireto: "00000000000191", // somente dígitos
      EmailIndireto: "contato@empresa.com.br",
      NomePrincipal: "Empresa ABC LTDA",
      NomeFantasia: "", // enviado vazio no fluxo atual
      Telefone: "11987654321", // somente dígitos (DDD+telefone)
      Cep: "01001000", // somente dígitos (8)
      Endereco: "Rua Exemplo",
      Numero: "100",
      // Complemento: "Conj. 12",         // omitido quando vazio
      Bairro: "Centro",
      Cidade: "São Paulo",
    },
  },
  {
    method: "POST",
    path: "/api-rscore/negative",
    title: "Criar Negativação",
    desc: "Cria uma negativação e já envia para o birô.",
    bodyExample: {
      cnpjClienteIndireto: "00000000000191",
      documentoDevedor: "12345678909",
      codigoNatureza: "60",
      dataVencimento: "2025-12-31",
      valor: 2500.5,
      contrato: "CTR-123",
      numeroBanco: "001",
      numeroAgencia: "1234",
      numeroCheque: "0001",
      numeroAlinea: "A1",
      numeroContaCorrente: "1234567",
      dataNascimento: "1985-08-15",
      nome: "Fulano de Tal",
      nomePai: "Nome do Pai",
      nomeMae: "Nome da Mãe",
      endereco: "Rua Exemplo, 100",
      bairro: "Centro",
      cidade: "São Paulo",
      cep: "01001000",
      uf: "SP",
      ddd: "11",
      telefone: "999999999",
      email: "devedor@example.com",
      usarCobranca: false,
      tipoCobranca: "S",
      valorJurosMulta: 0.0,
      percentualDesconto: 0.0,
    },
  },
  {
    method: "GET",
    path: "/api-rscore/negative/search-customer",
    title: "Consultar Cliente Indireto",
    desc: "Verifica no provedor se o cliente indireto da empresa está cadastrado.",
  },
  {
    method: "GET",
    path: "/api-rscore/negative/search-negative/:negativeId",
    title: "Consultar Status da Negativação",
    desc: "Consulta no provedor a situação da negativação e atualiza datas/status localmente.",
    pathParam: ":negativeId",
  },
  {
    method: "GET",
    path: "/api-rscore/negative/:id",
    title: "Obter Negativação por ID",
    desc: "Retorna um registro específico.",
    pathParam: ":id",
  },
  // ✅ endpoint de baixa
  {
    method: "POST",
    path: "/api-rscore/negative/:id/baixa",
    title: "Baixar Negativação",
    desc: "Define status BAIXADA, envia a baixa ao provedor e atualiza o registro. Use um dos códigos da seção 'Motivos de Baixa' abaixo.",
    pathParam: ":id",
    bodyExample: { motivoBaixa: "01" }, // utilize um dos códigos listados em MOTIVOS_BAIXA
  },
];

// --- Badge de método HTTP ---
const MethodBadge = ({ method }) => {
  const color =
    method === "GET"
      ? "bg-blue-600"
      : method === "POST"
      ? "bg-emerald-600"
      : method === "PUT"
      ? "bg-amber-600"
      : method === "DELETE"
      ? "bg-rose-600"
      : "bg-gray-700";
  return (
    <span
      className={`inline-block text-white ${color} px-2 py-0.5 rounded text-xs`}
    >
      {method}
    </span>
  );
};

// --- Card colapsável para cada endpoint ---
function CollapsibleEndpoint({ ep, baseURL, defaultOpen = false, idx }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        aria-expanded={open}
        aria-controls={`endpoint-panel-${idx}`}
      >
        <div className="flex items-center gap-3">
          {open ? (
            <HiChevronDown className="h-5 w-5 text-gray-500" />
          ) : (
            <HiChevronRight className="h-5 w-5 text-gray-500" />
          )}
          <div className="text-sm font-semibold">
            <MethodBadge method={ep.method} />{" "}
            <span className="ml-2">{ep.title}</span>
          </div>
        </div>
        <code className="text-[11px] bg-gray-50 border border-gray-200 px-2 py-1 rounded flex items-center gap-1">
          <HiOutlineLink className="opacity-70" />
          {baseURL}
          {ep.path}
        </code>
      </button>

      {open && (
        <div
          id={`endpoint-panel-${idx}`}
          className="px-4 pb-4 pt-1 border-t border-gray-100"
          role="region"
          aria-labelledby={`endpoint-header-${idx}`}
        >
          <p className="text-sm text-gray-600 mb-3">{ep.desc}</p>

          {ep.queryExample && (
            <div className="text-xs text-gray-700 mb-3">
              <div className="font-semibold mb-1">Exemplo de Query String:</div>
              <code className="bg-gray-50 border border-gray-200 px-2 py-1 rounded">
                {ep.queryExample}
              </code>
            </div>
          )}

          {ep.bodyExample && (
            <div className="mt-2">
              <div className="text-xs font-semibold text-gray-700 mb-1">
                Exemplo de Body (JSON):
              </div>
              <CopyBlock
                text={JSON.stringify(ep.bodyExample, null, 2)}
                theme={dracula}
                language="json"
                codeBlock
              />
            </div>
          )}

          <div className="mt-3">
            <div className="text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
              <HiOutlineCode />
              Exemplo cURL:
            </div>
            <CopyBlock
              theme={dracula}
              codeBlock
              text={[
                `curl -X ${ep.method} \\`,
                `  "${baseURL}${ep.path}${
                  ep.queryExample ? ep.queryExample : ""
                }" \\`,
                `  -H "Authorization: Bearer <seu-token-jwt>" \\`,
                `  -H "Content-Type: application/json"`,
                ep.bodyExample
                  ? `  -d '${JSON.stringify(ep.bodyExample)}'`
                  : "",
              ]
                .filter(Boolean)
                .join("\n")}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function NegativeDocs({ token }) {
  const baseURL = useMemo(() => window.location.origin, []);

  return (
    <div>
      <header className="mb-5 border-b border-gray-100 pb-6">
        <h1 className="text-2xl font-bold flex items-center gap-3 text-gray-900">
          Negativação — Documentação da API
        </h1>
        <p className="text-base text-gray-500 mt-2">
          Endpoints protegidos com <strong>JWT (Bearer)</strong> e escopos por
          empresa/representante.
        </p>
      </header>

      <section className="mb-6">
        <div className="text-sm font-mono bg-gray-50 border border-gray-200 rounded-md p-4 text-gray-800">
          <div className="mb-2 text-gray-500 font-semibold">
            🔐 Autenticação:
          </div>
          <div className="mt-2 bg-gray-50 border border-gray-100 rounded-md p-3 text-xs font-mono text-gray-800">
            Authorization: Bearer <em>&lt;seu-token-jwt&gt;</em>
          </div>
        </div>

        {token && (
          <div className="mt-3">
            <div className="font-semibold text-sm mb-1">
              🔗 Seu token (ambiente atual):
            </div>
            <CopyBlock text={token} theme={dracula} codeBlock />
          </div>
        )}
      </section>

      {/* Endpoints — todos começam colapsados */}
      <section className="space-y-3">
        {Endpoints.map((ep, idx) => (
          <CollapsibleEndpoint
            key={`${ep.method}-${ep.path}-${idx}`}
            ep={ep}
            baseURL={baseURL}
            idx={idx}
            defaultOpen={false}
          />
        ))}
      </section>

      {/* =========================
           TABELAS: Naturezas e Campos
         ========================= */}
      <section className="mt-10 space-y-10">
        {/* Naturezas */}
        <div>
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            🗂️ <span>Códigos de Natureza (Criação da negativação)</span>
          </h3>
          <div className="overflow-hidden rounded-xl border border-gray-200">
            <table className="min-w-full text-sm bg-white">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-5 py-3 text-left font-semibold border-b w-24">
                    Código
                  </th>
                  <th className="px-5 py-3 text-left font-semibold border-b w-24">
                    Sigla
                  </th>
                  <th className="px-5 py-3 text-left font-semibold border-b">
                    Descrição
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {NATUREZAS.sort(
                  (a, b) => Number(a.Codigo) - Number(b.Codigo)
                ).map((n) => (
                  <tr key={n.Codigo} className="hover:bg-gray-50 transition">
                    <td className="px-5 py-3 border-b">{n.Codigo}</td>
                    <td className="px-5 py-3 border-b">{n.Sigla}</td>
                    <td className="px-5 py-3 border-b">{n.Descricao}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Observação: para <strong>DC (22)</strong> (Devolução de Cheques), os
            campos de cheque tornam-se obrigatórios.
          </p>
        </div>

        {/* Campos */}
        <div>
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            📋{" "}
            <span>
              Campos: Obrigatórios x Opcionais (Criação da negativação)
            </span>
          </h3>
          <div className="overflow-hidden rounded-xl border border-gray-200">
            <table className="min-w-full text-sm bg-white">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-5 py-3 text-left font-semibold border-b w-64">
                    Nome
                  </th>
                  <th className="px-5 py-3 text-left font-semibold border-b w-48">
                    Tipo
                  </th>
                  <th className="px-5 py-3 text-left font-semibold border-b w-36">
                    Obrigatório
                  </th>
                  <th className="px-5 py-3 text-left font-semibold border-b">
                    Observações
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {FIELDS.map((f) => (
                  <tr key={f.nome} className="hover:bg-gray-50 transition">
                    <td className="px-5 py-3 border-b font-mono">{f.nome}</td>
                    <td className="px-5 py-3 border-b">{f.tipo}</td>
                    <td className="px-5 py-3 border-b">
                      <span
                        className={[
                          "inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold",
                          f.obrigatorio === "Sim"
                            ? "bg-green-100 text-green-800"
                            : f.obrigatorio === "Condicional"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-gray-100 text-gray-700",
                        ].join(" ")}
                      >
                        {f.obrigatorio}
                      </span>
                    </td>
                    <td className="px-5 py-3 border-b">{f.obs}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul className="text-xs text-gray-600 mt-3 list-disc ml-5 space-y-1">
            <li>
              <strong>Condicional (CPF):</strong>{" "}
              <code className="font-mono">dataNascimento</code> é obrigatório
              quando o documento do devedor é CPF.
            </li>
            <li>
              <strong>Condicional (DC/22):</strong> campos de cheque (
              <code className="font-mono">numeroBanco</code>,{" "}
              <code className="font-mono">numeroAgencia</code>,{" "}
              <code className="font-mono">numeroCheque</code>,{" "}
              <code className="font-mono">numeroAlinea</code>,{" "}
              <code className="font-mono">numeroContaCorrente</code>) tornam-se
              obrigatórios.
            </li>
            <li>
              <strong>Condicional (Cobrança):</strong>{" "}
              <code className="font-mono">tipoCobranca</code> é obrigatório
              quando <code className="font-mono">usarCobranca = true</code>.
            </li>
            <li>
              Envie documentos/CEP/telefone <strong>somente com dígitos</strong>
              ; datas no formato <code className="font-mono">YYYY-MM-DD</code>.
            </li>
          </ul>
        </div>
      </section>

      {/* =========================
           TABELA: Motivos de Baixa
         ========================= */}
      <section className="mt-10">
        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          🧾 <span>Motivos de Baixa</span>
        </h3>

        <div className="overflow-hidden rounded-xl border border-gray-200">
          <table className="min-w-full text-sm bg-white">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="px-5 py-3 text-left font-semibold border-b w-24">
                  Código
                </th>
                <th className="px-5 py-3 text-left font-semibold border-b w-64">
                  Motivo
                </th>
                <th className="px-5 py-3 text-left font-semibold border-b">
                  Descrição
                </th>
              </tr>
            </thead>
            <tbody className="text-gray-700">
              {MOTIVOS_BAIXA.map((m) => (
                <tr key={m.Codigo} className="hover:bg-gray-50 transition">
                  <td className="px-5 py-3 border-b font-mono">{m.Codigo}</td>
                  <td className="px-5 py-3 border-b">{m.Motivo}</td>
                  <td className="px-5 py-3 border-b">{m.Descricao}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-gray-600 mt-3">
          Use o campo <code className="font-mono">motivoBaixa</code> no body do
          endpoint{" "}
          <code className="font-mono">POST /api-rscore/negative/:id/baixa</code>{" "}
          com um dos códigos acima. Ex.:{" "}
          <code className="font-mono">{`{ "motivoBaixa": "01" }`}</code>.
        </p>
      </section>

      <section className="mt-10">
        <div className="bg-yellow-50 text-yellow-800 border border-yellow-200 rounded-md px-3 py-2 text-sm leading-relaxed">
          ⚠️ Reforço: Chamadas reais podem gerar cobrança conforme seu contrato
          (ex.: preço CV).
        </div>
      </section>
    </div>
  );
}
